import { useState, useEffect } from 'react';
import { Button, Panel } from '@/components/ui';
import { AvailabilityPicker } from './AvailabilityPicker';
import { generateRecurringWorkoutICS, downloadICS } from '@/utils/icsGenerator';
import type { TimeSlot } from './AvailabilityPicker';

export interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (slots: TimeSlot[]) => Promise<void>;
  initialSlots?: TimeSlot[];
}

export function BookingModal({
  isOpen,
  onClose,
  onSave,
  initialSlots = [],
}: BookingModalProps) {
  const [slots, setSlots] = useState<TimeSlot[]>(initialSlots);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [savedSlots, setSavedSlots] = useState<TimeSlot[]>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSlots(initialSlots);
      setShowSuccess(false);
      setSavedSlots([]);
    }
  }, [isOpen, initialSlots]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(slots);
      setSavedSlots(slots);
      setShowSuccess(true);
    } catch (error) {
      console.error('Failed to save availability:', error);
      alert('Failed to save your availability. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleExportCalendar = () => {
    if (savedSlots.length === 0) return;

    const icsContent = generateRecurringWorkoutICS(savedSlots);
    downloadICS(icsContent);
  };

  const handleClose = () => {
    setShowSuccess(false);
    setSavedSlots([]);
    onClose();
  };

  const meetsMinimum = slots.length >= 2;
  const hasExistingSlots = initialSlots.length > 0;

  return (
    <Panel
      isOpen={isOpen}
      onClose={handleClose}
      title={showSuccess ? 'Schedule Saved!' : (hasExistingSlots ? 'Update Your Schedule' : 'Book Your Sessions')}
      variant="modal"
      size={showSuccess ? 'md' : 'lg'}
    >
      {showSuccess ? (
        /* ── Success view ── */
        <div className="space-y-5">

          {/* Success header */}
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg flex-shrink-0">
              <svg
                className="w-6 h-6 text-green-600 dark:text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Your schedule is all set!
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                Your AI trainer will schedule workouts based on your availability. You'll
                receive phone call reminders before each session.
              </p>
            </div>
          </div>

          {/* ── Primary CTA: Add to Calendar ── */}
          <div className="rounded-xl border-2 border-primary-400 dark:border-primary-500 bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 p-5">
            <div className="flex flex-col items-center text-center gap-3">
              {/* Calendar icon */}
              <div className="p-3 bg-primary-100 dark:bg-primary-800/40 rounded-full">
                <svg
                  className="w-7 h-7 text-primary-600 dark:text-primary-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>

              <div>
                <p className="text-base font-semibold text-primary-900 dark:text-primary-200 mb-1">
                  Block workout time in your calendar
                </p>
                <p className="text-sm text-primary-700 dark:text-primary-400">
                  Download a recurring .ics file — opens in Google Calendar, Apple
                  Calendar, Outlook &amp; more. Includes 2-hour advance reminders.
                </p>
              </div>

              <Button
                variant="primary"
                size="lg"
                onClick={handleExportCalendar}
                className="w-full mt-1"
                data-testid="add-to-calendar-button"
              >
                <svg
                  className="w-5 h-5 mr-2 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Add to Calendar
              </Button>
            </div>
          </div>

          {/* What's next info box */}
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 rounded-lg p-4">
            <div className="flex gap-3">
              <svg
                className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm">
                <p className="text-blue-900 dark:text-blue-300 font-medium mb-1">
                  What's next?
                </p>
                <ul className="text-blue-800 dark:text-blue-400 space-y-1 list-disc list-inside">
                  <li>Your AI trainer will generate personalized workouts</li>
                  <li>Phone call reminders will arrive 2 hours before each session</li>
                  <li>Track your progress in the dashboard</li>
                  <li>Update your schedule anytime in Settings</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Done — secondary action */}
          <div className="flex justify-end pt-1">
            <Button
              variant="ghost"
              onClick={handleClose}
              className="w-full sm:w-auto"
            >
              Done
            </Button>
          </div>
        </div>
      ) : (
        /* ── Regular booking view ── */
        <div className="space-y-6">
          {/* Description */}
          <p className="text-sm text-gray-600 dark:text-slate-400">
            Select your preferred workout days and times. Your AI trainer is available
            24/7 and will schedule sessions based on your preferences.
          </p>

          {/* Availability Picker */}
          <AvailabilityPicker
            value={slots}
            onChange={setSlots}
            maxDays={6}
            minDays={2}
            disabled={saving}
          />

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 rounded-lg p-4">
            <div className="flex gap-3">
              <svg
                className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm">
                <p className="text-blue-900 dark:text-blue-300 font-medium mb-1">
                  How it works
                </p>
                <ul className="text-blue-800 dark:text-blue-400 space-y-1 list-disc list-inside">
                  <li>Select at least 2 days per week</li>
                  <li>Choose your preferred workout times</li>
                  <li>Your AI trainer will automatically schedule sessions</li>
                  <li>You'll receive phone call reminders before each workout</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-white/10">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={saving}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!meetsMinimum || saving}
              isLoading={saving}
              className="flex-1"
              data-testid="booking-modal-save-button"
            >
              {saving ? 'Saving...' : hasExistingSlots ? 'Update Schedule' : 'Save Preferences'}
            </Button>
          </div>
        </div>
      )}
    </Panel>
  );
}
